import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidV4 } from 'uuid';

export enum ToastType {
  'SUCCESS' = 'SUCCESS',
  'ERROR' = 'ERROR',
  'INFO' = 'INFO',
  'WARNING' = 'WARNING',
}

export type Toast = {
  id: string;
  type: ToastType;
  title?: string;
  text?: string;
  extraContent?: React.ReactNode;
};

export interface ToastStore {
  toasts: Toast[];
}

const initialState: ToastStore = {
  toasts: [],
};

/*************
 * reducers *
 *************/

export const addToastReducer = (
  state: ToastStore,
  action: PayloadAction<Toast>,
): ToastStore => {
  return {
    ...state,
    toasts: [...state.toasts, action.payload],
  };
};

export const addSuccessToastReducer = (
  state: ToastStore,
  action: PayloadAction<{
    title?: string;
    text: string;
    extraContent?: React.ReactNode;
  }>,
): ToastStore => {
  const toast: Toast = {
    id: uuidV4(),
    type: ToastType.SUCCESS,
    title: action.payload.title,
    text: action.payload.text,
    extraContent: action.payload.extraContent,
  };
  return addToastReducer(state, {
    type: 'toastStore/addToast',
    payload: toast,
  });
};

export const addErrorToastReducer = (
  state: ToastStore,
  action: PayloadAction<{
    title?: string;
    text: string;
    extraContent?: React.ReactNode;
  }>,
): ToastStore => {
  const toast: Toast = {
    id: uuidV4(),
    type: ToastType.ERROR,
    title: action.payload.title,
    text: action.payload.text,
    extraContent: action.payload.extraContent,
  };
  return addToastReducer(state, {
    type: 'toastStore/addToast',
    payload: toast,
  });
};

export const addInfoToastReducer = (
  state: ToastStore,
  action: PayloadAction<{
    title?: string;
    text: string;
    extraContent?: React.ReactNode;
  }>,
): ToastStore => {
  const toast: Toast = {
    id: uuidV4(),
    type: ToastType.INFO,
    title: action.payload.title,
    text: action.payload.text,
    extraContent: action.payload.extraContent,
  };
  return addToastReducer(state, {
    type: 'toastStore/addToast',
    payload: toast,
  });
};

export const addWarningToastReducer = (
  state: ToastStore,
  action: PayloadAction<{
    title?: string;
    text: string;
    extraContent?: React.ReactNode;
  }>,
): ToastStore => {
  const toast: Toast = {
    id: uuidV4(),
    type: ToastType.WARNING,
    title: action.payload.title,
    text: action.payload.text,
    extraContent: action.payload.extraContent,
  };
  return addToastReducer(state, {
    type: 'toastStore/addToast',
    payload: toast,
  });
};

export const removeToastReducer = (
  state: ToastStore,
  action: PayloadAction<{ id: string }>,
): ToastStore => {
  const filteredToasts = state.toasts.filter((toast) => {
    return toast.id !== action.payload.id;
  });
  return {
    ...state,
    toasts: filteredToasts,
  };
};

/*************
 * selectors *
 *************/
export const getToastsSelector = (state: ToastStore): Toast[] => state.toasts;

/*************
 * slice *
 *************/

export const toastStoreSlice = createSlice({
  name: 'toastStore',
  initialState,
  reducers: {
    addSuccess: addSuccessToastReducer,
    addError: addErrorToastReducer,
    addInfo: addInfoToastReducer,
    addWarning: addWarningToastReducer,
    removeToast: removeToastReducer,
  },
  selectors: {
    getToasts: getToastsSelector,
  },
});

export const { addError, addInfo, addSuccess, addWarning, removeToast } =
  toastStoreSlice.actions;
export const { getToasts } = toastStoreSlice.selectors;
export const toastStoreReducer = toastStoreSlice.reducer;
